import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { ApolloError } from "@apollo/client";
import { useViewer } from "~/providers/viewer";
import { useNavigate } from "react-router-dom";
import {
  useCreateOrganizationMutation,
  useOrganizationIdSuggestionLazyQuery,
} from "~/operations";
import { Checkbox, FormControlLabel, Grid, Link } from "@mui/material";
import { LoadingButton } from "~/components/loading-button";
import { SubmitHandler, useForm } from "react-hook-form";
import { ScopeType } from "~/hooks/useScope";
import CompanyField from "~/components/FormFields/CompanyField";
import { CreateFormInput } from "~/components/FormFields/types";
import {
  DescriptionField,
  DisplayNameField,
  IdField,
} from "~/components/FormFields";

const ORGANIZATION_ID_UNAVAILABLE = "This organization id is not available";

export function CreateOrganization() {
  const { refetch } = useViewer();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const hasCreateOrganizationPermissions = true;

  const [
    getOrganizationIdSuggestion,
    {
      data: organizationIdSuggestion,
      refetch: refetchOrganizationIdSuggestion,
    },
  ] = useOrganizationIdSuggestionLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [createOrganization, { loading: isCreateOrganizationLoading }] =
    useCreateOrganizationMutation();

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setError,
    formState: { errors, isValid },
  } = useForm<CreateFormInput>({
    mode: "onChange",
    defaultValues: {
      name: "",
      company: "",
      description: "",
      id: "",
      tac: false,
    },
  });

  useEffect(() => {
    const values = getValues();

    reset({
      ...values,
      id: organizationIdSuggestion?.organizationIDSuggestion?.id || "",
    });

    if (
      organizationIdSuggestion?.organizationIDSuggestion?.available !== true
    ) {
      setError("id", { message: ORGANIZATION_ID_UNAVAILABLE });
    }
  }, [organizationIdSuggestion]);

  const getIdSuggestion = async (id = "") => {
    // ID needs to be hyphenated to be valid
    const idAvailable = id.trim().split(" ").join("-").toLowerCase();
    const suggestion = await refetchOrganizationIdSuggestion({
      input: { idAvailable },
    });

    return suggestion?.data?.organizationIDSuggestion;
  };

  useEffect(() => {
    getOrganizationIdSuggestion({
      variables: {
        input: { idAvailable: "" },
      },
    });
  }, []);

  const handleFormSubmit: SubmitHandler<CreateFormInput> = async (values) => {
    try {
      const id = values.id.trim().split(" ").join("-").toLowerCase();
      const name = values.name;
      const company = values.company;
      const description = values.description || "";

      const createOrganizationResult = await createOrganization({
        variables: {
          input: {
            name,
            id,
            company,
            description,
          },
        },
      });
      const createdOrganization =
        createOrganizationResult.data?.createOrganization;

      if (!createdOrganization)
        throw new Error("Failed to create organization");

      enqueueSnackbar(`Successfully created organization: ${name}`, {
        variant: "success",
      });
      // we have to wait for viewer and settings to load again
      // before we can navigate to the new space
      await refetch();
      navigate(`/organization/spaces?organizationId=${createdOrganization.id}`);
    } catch (err) {
      if (err instanceof ApolloError) {
        err.graphQLErrors.map((e) => {
          enqueueSnackbar(e.message, { variant: "error" });
        });
      } else {
        enqueueSnackbar("Failed to create organization", { variant: "error" });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container size={12} gap={5}>
        <IdField
          errors={errors}
          register={register}
          idSuggestion={organizationIdSuggestion?.organizationIDSuggestion}
          onGenerateId={getIdSuggestion}
          unavailableErrorMessage={ORGANIZATION_ID_UNAVAILABLE}
          canEdit
          target={ScopeType.Organization}
        />

        <DisplayNameField
          errors={errors}
          register={register}
          target={ScopeType.Organization}
        />

        <CompanyField errors={errors} register={register} />

        <DescriptionField register={register} target={ScopeType.Organization} />

        <Grid size={12} textAlign="center">
          <FormControlLabel
            control={
              <Checkbox
                id="policy-accept-input"
                {...register("tac", { required: true })}
              />
            }
            label={
              <p>
                I have read and agree to the Mondoo{" "}
                <Link
                  href="https://mondoo.com/terms"
                  target="_blank"
                  rel="noopener"
                  sx={{ color: "inherit", textDecoration: "underline" }}
                >
                  Terms of Service
                </Link>{" "}
                and the{" "}
                <Link
                  href="https://mondoo.com/privacy"
                  target="_blank"
                  rel="noopener"
                  sx={{ color: "inherit", textDecoration: "underline" }}
                >
                  Privacy Policy
                </Link>
              </p>
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }} style={{ textAlign: "center" }}>
          <LoadingButton
            type="submit"
            buttonText="Create Organization"
            variant="contained"
            color="primary"
            disabled={!isValid || !hasCreateOrganizationPermissions}
            loading={isCreateOrganizationLoading}
          />
        </Grid>
      </Grid>
    </form>
  );
}
